import type { Project } from '@/store/AppContext';

const AREA_FIELD_PRIORITY = [
  'area',
  'estimatedAreaM2',
  'totalArea',
  'projectArea',
  'squareMeters',
  'surfaceArea',
] as const;

type ProjectAreaField = (typeof AREA_FIELD_PRIORITY)[number];
type ProjectAreaLike = Pick<Project, 'area' | 'laborItems'> &
  Partial<Record<ProjectAreaField, unknown>>;

const STANDARD_REFRESH_SCOPE =
  'odświeżenie mieszkania, dwukrotne malowanie, drobne zaprawki oraz zabezpieczenie powierzchni.';

function positiveNumber(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function resolveSavedProjectArea(project: ProjectAreaLike): number | null {
  for (const field of AREA_FIELD_PRIORITY) {
    const value = positiveNumber(project[field]);
    if (value !== null) {
      return value;
    }
  }

  const itemArea = project.laborItems?.find((item) => {
    const unit = String(item.unit || '').toLowerCase();
    return unit.includes('m') && positiveNumber(item.quantity) !== null;
  });

  return positiveNumber(itemArea?.quantity);
}

export function formatSavedProjectArea(project: ProjectAreaLike): string {
  const area = resolveSavedProjectArea(project);
  return area !== null ? `Powierzchnia robocza: ${formatArea(area)}` : 'Powierzchnia robocza: do uzupełnienia';
}

export function formatProjectScopeLabel(value: string | null | undefined, fallback: string): string {
  const text = normalizeProjectScope(value);
  return text || fallback;
}

function normalizeProjectScope(value: string | null | undefined): string {
  const source = value?.replace(/\s+/g, ' ').trim() ?? '';
  if (!source) {
    return '';
  }

  const searchable = normalizeSearchText(source);
  if (
    (searchable.includes('odswiezenie') || searchable.includes('odswierzenie')) &&
    searchable.includes('dwukrotne malowanie') &&
    searchable.includes('zapraw') &&
    searchable.includes('zabezpiec')
  ) {
    return STANDARD_REFRESH_SCOPE;
  }

  return source
    .replace(/\bod[śs]wie?r?[żz]enie\b/gi, 'odświeżenie')
    .replace(/\bzaprawi\b/gi, 'zaprawki')
    .replace(/\bzabezpieczanie\b/gi, 'zabezpieczenie')
    .replace(/\bzabezpieczania\b/gi, 'zabezpieczenia')
    .replace(/\bdwukrotne malowanie\s+drobne zaprawki\b/gi, 'dwukrotne malowanie, drobne zaprawki')
    .replace(/\bdrobne zaprawki\s*,\s*zabezpieczenie\b/gi, 'drobne zaprawki oraz zabezpieczenie')
    .replace(/\s+([,.])/g, '$1')
    .replace(/,([^\s])/g, ', $1')
    .trim();
}

function normalizeSearchText(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatArea(value: number): string {
  return value.toLocaleString('pl-PL', { maximumFractionDigits: 1 }) + ' m²';
}
